/* eslint-disable no-console */
import {
    buildPaymentRequest,
    setSessionListeners,
    setEntryPoint,
    shoppayBtnDisabledStyle,
    shoppayMutationObserver,
    IShopPaySession,
    IPaymentRequest
} from '../helpers/shoppayHelper';

const ENTRY_POINT_REGULAR = 'regular';
const ENTRY_POINT_EXPRESS = 'checkout';

/**
 * @param PaymentAccordionItem Base widget for extending
 * @returns ShopPay Payment Accordion Item widget
 */
function PaymentAccordionItemShopPayClassCreator(PaymentAccordionItem: import('widgets/checkout/PaymentAccordionItem').TPaymentAccordionItem) {
    const SHOPPAY_BUTTON_CONTAINER_ID = 'shop-pay-button-container';
    const SHOPPAY_EXPRESS_BUTTON_CONTAINER_ID = 'shop-pay-button-container-express';

    /**
     * Initializes ShopPay configuration.
     */
    function initShopPayConfig(): void {
        if (window.ShopPay?.PaymentRequest?.configure) {
            window.ShopPay.PaymentRequest.configure({
                shopId: window.shoppayClientRefs?.preferences?.shoppayStoreId,
                clientId: window.shoppayClientRefs?.preferences?.shoppayClientId,
                debug: window.shoppayClientRefs?.preferences?.shoppayModalDebugEnabled
            });
        } else if (window.ShopPay?.PaymentRequest) {
            console.warn('ShopPay SDK PaymentRequest is available, but "configure" method is missing.');
        } else {
            console.error('ShopPay SDK or PaymentRequest not available for configuration.');
        }
    }

    /**
     * Initializes the ShopPay button.
     */
    function initShopPayButton(): void {
        const paymentSelectors = [`#${SHOPPAY_BUTTON_CONTAINER_ID}`, `#${SHOPPAY_EXPRESS_BUTTON_CONTAINER_ID}`];
        const isBuyNow = window.shoppayClientRefs?.constants?.isBuyNow ?? false;

        const createButton = window.ShopPay?.PaymentRequest?.createButton;

        if (typeof createButton === 'function') {
            paymentSelectors.forEach(selector => {
                const button = createButton({ buyWith: isBuyNow });
                const container = document.querySelector(selector);

                if (container) {
                    container.replaceChildren();
                }

                button?.render(selector);
                shoppayMutationObserver(selector);

                const csrfToken = container?.parentElement?.dataset?.csrfToken;
                const isForceDisable = !csrfToken || csrfToken === 'null';

                shoppayBtnDisabledStyle(container as HTMLElement, undefined, isForceDisable);
            });
        } else if (window.ShopPay?.PaymentRequest) {
            console.warn('ShopPay SDK PaymentRequest is available, but "createButton" method is missing.');
        } else {
            console.error('ShopPay SDK or PaymentRequest not available for button creation.');
            // Optionally disable the button container if SDK is missing
            const buttonContainer = document.getElementById(SHOPPAY_BUTTON_CONTAINER_ID);

            if (buttonContainer) {
                shoppayBtnDisabledStyle(buttonContainer, undefined, true);
            }
        }
    }

    /**
     * Initializes ShopPay email recognition on the checkout page.
     */
    function initShopPayEmailRecognition(): void {
        /*  If your checkout is not built with SFRA or you have customized and removed the 'email-guest'
            id on the email input you will need to update the id value for emailInputId

            Note: A 1.5 second delay has been added before attaching to the email trigger to the email field to
            accommodate users who may have incorporated browser autofills. The pop-up will appear if the field
            is autofilled with a recognized email address
        */
        const emailInputId = 'dwfrm_shipping_email';
        const emailInput = document.getElementById(emailInputId);

        if (emailInput && window.ShopPay?.PaymentRequest?.createLogin) {
            setTimeout(() => {
                // Re-check PaymentRequest and createLogin within the timeout to satisfy TypeScript's flow analysis
                if (window.ShopPay?.PaymentRequest?.createLogin) {
                    const login = window.ShopPay.PaymentRequest.createLogin({ emailInputId: emailInputId });

                    login?.render('#shop-pay-login-container');
                }
            }, 1500);
        } else if (!emailInput) {
            console.warn(`ShopPay email recognition: Email input with ID "${emailInputId}" not found.`);
        } else if (window.ShopPay?.PaymentRequest) {
            console.warn('ShopPay SDK PaymentRequest is available, but "createLogin" method is missing.');
        } else {
            console.error('ShopPay SDK or PaymentRequest not available for email recognition.');
        }
    }

    /**
     * Initializes the ShopPay session.
     *
     * @param initialPaymentRequest - The initial payment request object.
     * @returns The created ShopPay session or null if creation failed.
     */
    function initShopPaySession(initialPaymentRequest: IPaymentRequest): IShopPaySession | null {
        if (window.ShopPay?.PaymentRequest?.createSession) {
            const shoppaySession = window.ShopPay.PaymentRequest.createSession({
                paymentRequest: initialPaymentRequest
            });

            // The createSession method itself can return null as per the interface

            if (shoppaySession) {
                setSessionListeners(shoppaySession);

                return shoppaySession;
            } else {
                console.error('Failed to create ShopPay session.');
                // Disable button if session creation fails
                shoppayBtnDisabledStyle(document.getElementById(SHOPPAY_BUTTON_CONTAINER_ID), undefined, true);

                return null;
            }
        } else if (window.ShopPay?.PaymentRequest) {
            console.warn('ShopPay SDK PaymentRequest is available, but "createSession" method is missing.');

            return null;
        } else {
            console.error('ShopPay SDK or PaymentRequest not available for session creation.');
            // Disable button if SDK is missing
            shoppayBtnDisabledStyle(document.getElementById(SHOPPAY_BUTTON_CONTAINER_ID), undefined, true);

            return null;
        }
    }

    /**
     * Initializes the entire ShopPay integration.
     * This includes SDK configuration, button rendering, email recognition (on checkout), and session creation.
     */
    async function initShopPay() {
        // Ensure ShopPay SDK is available
        if (!window.ShopPay) {
            console.error('ShopPay SDK is not loaded.');
            // Disable the button container if SDK is missing
            const buttonContainer = document.getElementById(SHOPPAY_BUTTON_CONTAINER_ID);

            if (buttonContainer) {
                shoppayBtnDisabledStyle(buttonContainer, undefined, true);
            }

            return;
        }

        initShopPayConfig();
        initShopPayButton();

        // Check if on checkout page and email input exists for email recognition
        const isCheckoutPage = window.shoppayClientRefs?.constants?.isCheckout ?? false;
        const emailInput = document.getElementById('dwfrm_shipping_email');

        if (isCheckoutPage && emailInput) {
            initShopPayEmailRecognition();
        }

        const paymentRequestResponse = await buildPaymentRequest(null);
        const responseJSON = paymentRequestResponse?.responseJSON ?? null;

        if (responseJSON && !responseJSON.error && responseJSON.paymentRequest) {
            const initialPaymentRequest = window.ShopPay.PaymentRequest.build(responseJSON.paymentRequest);

            initShopPaySession(initialPaymentRequest);
        } else {
            console.error('Failed to build initial payment request for ShopPay session.', responseJSON?.errorMsg);
            // Disable button if initial request fails
            shoppayBtnDisabledStyle(document.getElementById(SHOPPAY_BUTTON_CONTAINER_ID), undefined, true);
        }
    }

    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentAccordionItemShopPay
     * @augments PaymentAccordionItem
     * @classdesc Represents PaymentAccordionItemShopPay component with the next features:
     * 1. Handles payment tab submit (though ShopPay uses modal flow, not traditional form submit)
     * 2. Validates payment tab child elements
     * 3. Initializes ShopPay button and session on checkout page.
     *
     * @example <caption>Example of PaymentAccordionItemShopPay widget usage</caption>
     *  <section
     *      data-widget="paymentAccordionItem${paymentOption.ID}"
     *      data-widget-event-closeallitems="closeItems"
     *      data-widget-event-togglebillingform="toggleBillingForm"
     *      data-widget-event-accordionitemupdate="updateFocusedItem"
     *      data-expanded="false"
     *      data-method-id="${paymentOption.ID}"
     *      data-method-name="${paymentOption.name}"
     *      data-skip-billing-address="${paymentOption.skipBillingAddress}"
     *      data-widget-event-openpanel="hidePaymentError"
     *      data-tau-payment-id="${paymentOption.ID}"
     *      class="b-payment_accordion-item"
     *  >
     *      <isinclude template="checkout/billing/paymentOptions/paymentTitle_ShopPay"/>
     *      <div
     *          id="payment-details-${paymentOption.ID}"
     *          data-ref="accordionItemPanel"
     *          class="b-payment_accordion-content"
     *          data-tau="payment-details-${paymentOption.ID}"
     *      >
     *          <div class="b-payment_accordion-content_inner" data-ref="accordionItemPanelInner">
     *              <div class="shoppay-btn_container">
     *                  <isinclude template="checkout/billing/paymentOptions/shoppayButton" />
     *              </div>
     *           </div>
     *      </div>
     *  </section>
     */
    class PaymentAccordionItemShopPay extends PaymentAccordionItem {
        /**
         * @description Handles ShopPay payment tab submit.
         * @returns result - An empty array as the payment is handled by the ShopPay modal.
         */
        process(): Promise<[]> {
            return Promise.resolve([]);
        }

        /**
         * Initializes the ShopPay Payment Accordion Item widget.
         * Sets up ShopPay configuration, button, email recognition, and session.
         */
        init() {
            super.init();

            initShopPay();

            this.eventBus().on('shoppay.click.express', 'onShopPayClickExpress');
            this.eventBus().on('shoppay.click.checkout', 'onShopPayClickCheckout');
        }

        /**
         * @description Validates ShopPay payment tab child elements.
         * @returns validation result - Always true as validation is external.
         */
        isChildrenValid(): boolean {
            return true;
        }

        onShopPayClickExpress() {
            setEntryPoint(ENTRY_POINT_EXPRESS);
        }

        onShopPayClickCheckout() {
            setEntryPoint(ENTRY_POINT_REGULAR);
        }
    }

    return PaymentAccordionItemShopPay;
}

export type TPaymentAccordionItemShopPay = ReturnType<typeof PaymentAccordionItemShopPayClassCreator>;
export type TPaymentAccordionItemShopPayInstance = InstanceType<TPaymentAccordionItemShopPay>;
export default PaymentAccordionItemShopPayClassCreator;
